import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { tradeSuggestionsApi } from '@/services/api';
import { Recommendation, StrategyData, TradeSuggestionsItem, TradeSuggestionsResponse } from '@/types/client';
import { formatDate, formatNumber, formatPrice as formatPriceWithCurrency } from '@/utils/formatters';
import { isGlobalCountryCode } from '@/utils/country';
import { PageFrame, PageHeader, Surface, Button, Pill, Tag, Note, Placeholder } from '@/components/ui';
import DataTable, { ColumnDef } from '@/components/utils/DataTable';
import { DetailSection, DetailField } from '@/components/utils/PageCard';
import css from './TradeSuggestions.module.css';
import { suggestionTone } from './tradeSuggestionColumns';

/** One row of the strategy-holdings table: the code plus its spread. */
interface StrategyRow extends StrategyData {
  code: string;
}

const TradeSuggestionsDetail: React.FC = () => {
  const { isin } = useParams<{ isin: string }>();
  const navigate = useNavigate();

  const [data, setData] = useState<TradeSuggestionsItem | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isin) {
      setError('ISIN parameter is required');
      setLoading(false);
      return;
    }

    let cancelled = false;

    const loadTradeSuggestionsDetail = async () => {
      try {
        setLoading(true);
        setError(null);

        const response: TradeSuggestionsResponse = await tradeSuggestionsApi.getTradeSuggestions({
          isin_list: isin
        });

        if (cancelled) return;

        if (response.data && response.data.length > 0) {
          setData(response.data[0]);
        } else {
          setError('No trade suggestions found for this company');
        }
      } catch (err: unknown) {
        if (cancelled) return;
        console.error('Error loading trade suggestions detail:', err);
        const errorMessage = (err as { response?: { data?: { message?: string } } })?.response?.data?.message || 'Failed to load trade suggestions detail';
        setError(errorMessage);
        toast.error(errorMessage);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void loadTradeSuggestionsDetail();
    return () => { cancelled = true; };
  }, [isin]);

  const strategyRows = useMemo<StrategyRow[]>(
    () => Object.entries(data?.strategies ?? {}).map(([code, strategy]) => ({ code, ...strategy })),
    [data],
  );

  const strategyColumns = useMemo<ColumnDef<StrategyRow>[]>(() => [
    { key: 'code', header: 'Strategy', render: row => <Tag>{row.code}</Tag> },
    { key: 'min', header: 'Min %', align: 'right', render: row => `${formatNumber(row.min_client_holding, 2)}%` },
    { key: 'median', header: 'Median %', align: 'right', render: row => `${formatNumber(row.median_client_holding, 2)}%` },
    { key: 'max', header: 'Max %', align: 'right', render: row => `${formatNumber(row.max_client_holding, 2)}%` },
    { key: 'clients', header: 'Clients', align: 'right', render: row => formatNumber(row.client_count, 0) },
  ], []);

  const backAction = (
    <Button onClick={() => navigate('/trade-suggestions')}>Back to Trade Suggestions</Button>
  );

  if (loading) {
    return (
      <PageFrame header={<PageHeader title="Trade Suggestion" actions={backAction} />}>
        <Surface fill>
          <Placeholder kind="loading" message="Loading trade suggestion…" />
        </Surface>
      </PageFrame>
    );
  }

  if (error || !data) {
    return (
      <PageFrame header={<PageHeader title="Trade Suggestion" actions={backAction} />}>
        <Surface padded fill>
          <Note tone="error">{error ?? 'No data found.'}</Note>
        </Surface>
      </PageFrame>
    );
  }

  // Global (non-Indian) companies show the ISO currency code after the price, e.g.
  // "100.00 (USD)": the LTP uses ltp_currency_code, recommendation prices use the raw
  // currency_code. Domestic companies keep the rupee sign.
  const isGlobal = isGlobalCountryCode(data.country_code);
  const formatPrice = (value: number, code: string | null | undefined): string =>
    formatPriceWithCurrency(value, code, isGlobal);

  const renderRecommendation = (recommendation: Recommendation, index: number) => (
    <div className={css['rec-card']} key={`${recommendation.recommended_by}-${recommendation.rec_date}-${index}`}>
      <div className={css['rec-head']}>
        <span className={css['rec-who']}>{recommendation.recommended_by}</span>
        <span className={css['rec-marks']}>
          {recommendation.recommendation_type && <Tag>{recommendation.recommendation_type}</Tag>}
          {recommendation.rec_date && (
            <Pill tone="info" solid>{formatDate(recommendation.rec_date)}</Pill>
          )}
        </span>
      </div>

      <div className={css['rec-grid']}>
        <DetailField label="Decision" value={recommendation.decision} />
        <DetailField
          label="Decision Price"
          value={recommendation.decision_price ? formatPrice(recommendation.decision_price, data.currency_code) : undefined}
        />
        <DetailField
          label="Target Price"
          value={recommendation.target_price != null ? formatPrice(recommendation.target_price, data.currency_code) : undefined}
        />
        <DetailField
          label="Target Weight"
          value={recommendation.target_weight != null ? `${formatNumber(recommendation.target_weight, 2)}%` : undefined}
        />
        <DetailField
          label="Target Date"
          value={recommendation.target_date ? formatDate(recommendation.target_date) : undefined}
        />
        <DetailField label="Estimate Type" value={recommendation.estimate_type ?? undefined} />
      </div>

      {recommendation.comment && <p className={css['rec-comment']}>{recommendation.comment}</p>}

      {Object.keys(recommendation.suggestions ?? {}).length > 0 && (
        <div>
          <span className={css['field-label']}>Trade suggestions</span>
          <div className={css['sug-grid']}>
            {Object.entries(recommendation.suggestions).map(([strategyCode, suggestion]) => (
              <span className={css['sug-item']} key={strategyCode}>
                <span className={css['sug-code']}>{strategyCode}</span>
                {suggestion.trade_type || suggestion.trade_description ? (
                  <Pill tone={suggestionTone(suggestion.trade_type)} title={suggestion.trade_description || undefined}>
                    {suggestion.trade_type || suggestion.trade_description}
                  </Pill>
                ) : (
                  <span>No trade</span>
                )}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  return (
    <PageFrame
      header={
        <PageHeader
          title={`${data.symbol} — ${data.company_name}`}
          subtitle={`Recommendations and strategy holdings for ${data.isin_number}.`}
          actions={backAction}
          stats={[
            { label: 'LTP', value: data.ltp != null ? formatPrice(data.ltp, data.ltp_currency_code) : '—',
              hint: data.ltp_timestamp ? `as of ${formatDate(data.ltp_timestamp)}` : undefined },
            { label: 'Category', value: data.category || '—' },
            { label: 'Status', value: data.is_invested ? 'Invested' : 'Not invested' },
            { label: 'Recommendations', value: formatNumber(data.recommendations?.length ?? 0, 0) },
          ]}
        />
      }
    >
      <Surface padded>
        {/* The LTP, category and status are on the header tiles above — this
            section carries what those cannot: identifiers and ownership. */}
        <DetailSection title="Company">
          <DetailField label="ISIN" value={data.isin_number} mono />
          <DetailField label="Symbol" value={data.symbol} mono />
          <DetailField label="Country" value={data.country_code} />
          <DetailField label="NSE Code" value={data.nse_code} mono />
          <DetailField label="BSE Code" value={data.bse_code} mono />
          <DetailField label="LTP Updated At" value={data.ltp_timestamp ? formatDate(data.ltp_timestamp) : undefined} />
          {/* The assignment, plus who gave the latest call when that is someone else. */}
          <DetailField label="Analyst" value={data.analyst_name} />
          <DetailField label="IC User" value={data.ic_user_name} />
          {data.latest_rec_analyst_name && data.latest_rec_analyst_name !== data.analyst_name && (
            <DetailField label="Latest Rec — Analyst" value={data.latest_rec_analyst_name} />
          )}
          {data.latest_rec_ic_user_name && data.latest_rec_ic_user_name !== data.ic_user_name && (
            <DetailField label="Latest Rec — IC User" value={data.latest_rec_ic_user_name} />
          )}
        </DetailSection>
      </Surface>

      {strategyRows.length > 0 && (
        <Surface title="Strategy Holdings" meta={`${strategyRows.length} strategies`}>
          <DataTable<StrategyRow>
            embedded
            hideSearch
            hideFooter
            data={strategyRows}
            columns={strategyColumns}
            rowKey={row => row.code}
            totalCount={strategyRows.length}
            loading={false}
            currentPage={1}
            totalPages={1}
            onPageChange={() => undefined}
            pageSize={strategyRows.length}
            sortField=""
            sortDir="asc"
            onSort={() => undefined}
          />
        </Surface>
      )}

      <Surface title="Recommendations" meta={`${data.recommendations?.length ?? 0} in range`} padded>
        {data.recommendations && data.recommendations.length > 0 ? (
          <div className={css['rec-list']}>
            {data.recommendations.map(renderRecommendation)}
          </div>
        ) : (
          <Placeholder message="No recommendations for this company in the selected range." />
        )}
      </Surface>
    </PageFrame>
  );
};

export default TradeSuggestionsDetail;
